import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CaretDown, CaretRight, Buildings, CheckCircle, XCircle, PaperPlaneTilt, Prohibit, ArrowCounterClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SectionHeader, ConfirmDialog } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const STATUS = {
  approved: ["Active", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  pending: ["Awaiting approval", "bg-amber-50 text-amber-800 border-amber-300"],
  rejected: ["Rejected", "bg-rose-50 text-rose-700 border-rose-200"],
  disabled: ["Disabled", "bg-zinc-100 text-zinc-600 border-zinc-300"],
  invited: ["Invited", "bg-sky-50 text-sky-700 border-sky-200"],
};

const StatusPill = ({ status, testid }) => {
  const [label, cls] = STATUS[status] || [status, "bg-zinc-100 text-zinc-600 border-zinc-300"];
  return <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 border rounded-sm whitespace-nowrap ${cls}`} data-testid={testid}>{label}</span>;
};

function UserActions({ u, act }) {
  if (u.is_self) return <span className="text-xs text-muted-foreground">You</span>;
  return (
    <div className="flex flex-wrap gap-1.5 justify-end">
      {u.approval_status === "pending" && !u.disabled && (<>
        <Button size="sm" className="h-7 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act({ kind: "approve", u })} data-testid={`acct-approve-${u.user_id}`}><CheckCircle size={14} className="mr-1" />Approve</Button>
        <Button size="sm" variant="outline" className="h-7 rounded-sm border-rose-300 text-rose-700" onClick={() => act({ kind: "reject", u })} data-testid={`acct-reject-${u.user_id}`}><XCircle size={14} className="mr-1" />Reject</Button>
        <Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={() => act({ kind: "resend", u })} data-testid={`acct-resend-${u.user_id}`}><PaperPlaneTilt size={14} className="mr-1" />Resend</Button>
      </>)}
      {u.disabled
        ? <Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={() => act({ kind: "enable", u })} data-testid={`acct-enable-${u.user_id}`}><ArrowCounterClockwise size={14} className="mr-1" />Re-enable</Button>
        : <Button size="sm" variant="outline" className="h-7 rounded-sm text-zinc-600" onClick={() => act({ kind: "disable", u })} data-testid={`acct-disable-${u.user_id}`}><Prohibit size={14} className="mr-1" />Disable</Button>}
    </div>
  );
}

function CompanyProfilePanel({ p, testid }) {
  if (!p) return null;
  const addr = [p.address, p.address2, [p.city, p.state, p.postcode].filter(Boolean).join(" "), p.country].filter(Boolean).join(", ");
  const rows = [["Legal name", p.legal_name], ["ABN / Tax no.", p.tax_number], ["Registration no.", p.registration_number], ["Address", addr], ["Phone", p.phone], ["Email", p.email], ["Website", p.website], ["Currency", p.currency || "AUD"]];
  return (
    <div className="px-4 py-4 bg-muted/30 border-t border-border flex flex-col sm:flex-row gap-4" data-testid={testid}>
      {p.logo ? <img src={p.logo} alt="" className="h-14 w-14 object-contain border border-border bg-white rounded-sm shrink-0" /> : null}
      <dl className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2 text-sm flex-1 min-w-0">
        {rows.map(([k, v]) => (
          <div key={k} className={k === "Address" ? "col-span-2" : ""}>
            <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{k}</dt>
            <dd className="break-words">{v || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function CompanyGroup({ g, open, toggle, act }) {
  const waiting = g.users.filter((u) => u.status === "pending").length;
  const key = g.key;
  return (
    <div className="border border-border rounded-sm bg-card" data-testid={`acct-company-${key}`}>
      <button type="button" onClick={toggle} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors" data-testid={`acct-company-toggle-${key}`}>
        {open ? <CaretDown size={16} /> : <CaretRight size={16} />}
        <Buildings size={18} className="text-primary shrink-0" />
        <span className="font-semibold flex-1 min-w-0 break-words">{g.company}</span>
        {waiting > 0 && <span className="hidden sm:inline"><StatusPill status="pending" testid={`acct-company-waiting-${key}`} /></span>}
        {waiting > 0 && <span className="sm:hidden h-2 w-2 rounded-full bg-amber-500 shrink-0" title="Awaiting approval" />}
        <span className="text-xs text-muted-foreground tabular whitespace-nowrap" data-testid={`acct-company-count-${key}`}>{g.users.length} user{g.users.length === 1 ? "" : "s"}</span>
      </button>
      {open && <CompanyProfilePanel p={g.profile} testid={`acct-company-profile-${key}`} />}
      {open && (
        <div className="border-t border-border divide-y divide-border">
          {g.users.map((u) => (
            <div key={u.user_id} className="px-4 py-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-4" data-testid={`acct-user-${u.user_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{u.name || u.email}</div>
                <div className="text-xs text-muted-foreground truncate">{u.email}{u.requested_company ? ` · requested “${u.requested_company}”` : ""}</div>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {g.tenant_id && <span className="capitalize">{u.role}</span>}
                <span>{u.auth_provider === "google" ? "Google" : "Email"}</span>
                <span className="tabular">{(u.created_at || "").slice(0, 10)}</span>
                <StatusPill status={u.status} testid={`acct-status-${u.user_id}`} />
              </div>
              <UserActions u={u} act={act} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CONFIRM = {
  approve: (u) => [`Approve ${u.email}?`, "They get their own company with starter data and an email saying they can sign in.", "Approve"],
  reject: (u) => [`Reject ${u.email}?`, "They are told their request was declined and can't sign in.", "Reject"],
  disable: (u) => [`Disable ${u.email}?`, "They are signed out and can't sign in until re-enabled. Nothing is deleted.", "Disable"],
  enable: (u) => [`Re-enable ${u.email}?`, "They can sign in again.", "Re-enable"],
};

export default function Accounts() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [openKeys, setOpenKeys] = useState({});
  const [q, setQ] = useState("");
  const [confirm, setConfirm] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["accounts-admin"], queryFn: async () => (await api.get("/accounts-admin")).data, enabled: !!user?.is_accounts_admin });

  const run = useMutation({
    mutationFn: async ({ kind, u }) => {
      if (kind === "approve" || kind === "reject") return (await api.post(`/accounts-admin/${u.user_id}/decide`, { decision: kind })).data;
      if (kind === "resend") return (await api.post(`/accounts-admin/${u.user_id}/resend-approval`)).data;
      return (await api.post(`/accounts-admin/${u.user_id}/disabled`, { disabled: kind === "disable" })).data;
    },
    onSuccess: (_r, { kind, u }) => {
      qc.invalidateQueries({ queryKey: ["accounts-admin"] });
      toast.success({ approve: "Approved", reject: "Rejected", resend: "Approval email resent", disable: "Account disabled", enable: "Account re-enabled" }[kind] + ` — ${u.email}`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (user && !user.is_accounts_admin) return <Navigate to="/dashboard" replace />;
  const term = q.trim().toLowerCase();
  const companies = (data?.companies || [])
    .map((g) => ({ ...g, users: term && !g.company.toLowerCase().includes(term) ? g.users.filter((u) => `${u.email} ${u.name}`.toLowerCase().includes(term)) : g.users }))
    .filter((g) => g.users.length);
  const pendingTotal = (data?.companies || []).reduce((n, g) => n + g.users.filter((u) => u.status === "pending").length, 0);
  const act = (a) => (a.kind === "resend" ? run.mutate(a) : setConfirm(a));
  const cfg = confirm ? CONFIRM[confirm.kind](confirm.u) : [];

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <SectionHeader title="Accounts" />
      <p className="-mt-4 text-sm text-muted-foreground" data-testid="accounts-summary">{data ? `${data.total} accounts across ${data.companies.filter((g) => g.tenant_id).length} companies${pendingTotal ? ` · ${pendingTotal} awaiting approval` : ""}` : ""}</p>
      <div className="relative max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company, name or email" className="pl-9 rounded-sm" data-testid="accounts-search" />
      </div>
      {isLoading ? <div className="text-sm text-muted-foreground" data-testid="accounts-loading">Loading accounts…</div> : (
        <div className="space-y-3">
          {companies.map((g) => {
            const key = g.key;
            const open = openKeys[key] ?? (g.key === "pending" || !!term);
            return <CompanyGroup key={key} g={g} open={open} toggle={() => setOpenKeys((s) => ({ ...s, [key]: !open }))} act={act} />;
          })}
          {!companies.length && <div className="text-sm text-muted-foreground" data-testid="accounts-empty">No accounts match.</div>}
        </div>
      )}
      <ConfirmDialog open={!!confirm} onOpenChange={(v) => { if (!v) setConfirm(null); }} title={cfg[0]} description={cfg[1]} confirmLabel={cfg[2]} testid="accounts-confirm" onConfirm={() => run.mutate(confirm)} />
    </div>
  );
}
